import type { ChecksLongData } from "@stryker/core/types/compositeSections";
import type { PolarYesNoUndefined } from "@stryker/core/types/section";
import { useEffect, useState } from "react";
import { mergeAll } from "remeda";
import { Button } from "@/components/ui/button";
import AxialLoadsChecks, {
  type AxialLoadsPolar,
} from "@/pages/admin/compositeJob/components/Checks/sub/AxialLoadsChecks";
import BuriedChecks, { type BuriedChecksPolar } from "@/pages/admin/compositeJob/components/Checks/sub/BuriedChecks";
import ExcludeComponentStrengthChecks, {
  type ExcludeComponentStrengthPolar,
} from "@/pages/admin/compositeJob/components/Checks/sub/ExcludeComponentStrength";
import ExtraLayersChecks, {
  type ExtraLayesPolar,
} from "@/pages/admin/compositeJob/components/Checks/sub/ExtraLayersChecks";
import { LeakingChecks, type LeakingPolar } from "@/pages/admin/compositeJob/components/Checks/sub/LeakingChecks";
import PressureCyclesChecks, {
  type PressureCyclesPolar,
} from "@/pages/admin/compositeJob/components/Checks/sub/PressureCyclesChecks";
import ProceedWithOtherChecks, {
  type ProceedWithNextPolar,
} from "@/pages/admin/compositeJob/components/Checks/sub/ProceedWithOtherChecks";
import ShutDownChecks, { type ShutDownPolar } from "@/pages/admin/compositeJob/components/Checks/sub/ShutDownChecks";
import StrainsChecks, { type StrainsChecksPolar } from "@/pages/admin/compositeJob/components/Checks/sub/StrainsChecks";
import { UtChecks, type UtPolar } from "@/pages/admin/compositeJob/components/Checks/sub/UtChecks";
import { VacuumChecks, type VacuumPolar } from "@/pages/admin/compositeJob/components/Checks/sub/VacuumChecks";
import useCUCompositeJobStore from "@/store/useCUCompositeJobStore";

const utInitial: UtPolar = {
  do_you_have_ut_reading_info: undefined,
  calculate_at_worst_case: undefined,
  is_ut_reading_less_than_04: undefined,
  per_code_line_leaking_do_you_know_diameter: undefined,
};

const leakingInitial: LeakingPolar = {
  component_leaking: undefined,
  is_pinhole_leak: undefined,
  component_patched_or_repaired: undefined,
};

const shutDownInitial: ShutDownPolar = {
  is_component_shutdown_during_repair: undefined,
};

const extraLayesInitial: ExtraLayesPolar = {
  add_extraLayers: undefined,
};

const proceedWithOtherChecksInitial: ProceedWithNextPolar = {
  do_you_wish_to_answer_more_questions: undefined,
};
const buriedInitial: BuriedChecksPolar = {
  is_component_buried: undefined,
};
const axialInitial: AxialLoadsPolar = {
  design_axial_loads: undefined,
  is_component_straight_line: undefined,
  do_you_want_to_take_into_account_any_additional_forces_and_moments: undefined,
};
const vacuumInitial: VacuumPolar = {
  do_you_want_to_design_for_external_loads_or_vacuum: undefined,
};
const pressureCyclesInitial: PressureCyclesPolar = {
  do_you_want_to_design_for_constant_pressure_cycles: undefined,
  are_the_expected_cycles_more_than_7000_over_design_life: undefined,
};

const strainsInitial: StrainsChecksPolar = {
  do_you_want_to_design_for_continous_long_term_strains: undefined,
};

const excludeComponentStrengthInitial: ExcludeComponentStrengthPolar = {
  do_you_want_to_completely_exclude_the_components_strength_for_design: undefined,
  do_you_want_to_take_into_account_any_additional_forces_and_moments: undefined,
};

export default function CompliantRepair() {
  const [utQuestions, setUtQuestions] = useState<UtPolar>(utInitial);
  const [leakingQuestions, setLeakingQuestions] = useState<LeakingPolar>(leakingInitial);
  const [shutDownQuestions, setShutDownQuestions] = useState<ShutDownPolar>(shutDownInitial);
  const [extraLayesQuestions, setExtraLayesQuestions] = useState<ExtraLayesPolar>(extraLayesInitial);
  const [proceedWithOtherChecksQuestions, setProceedWithOtherChecks] =
    useState<ProceedWithNextPolar>(proceedWithOtherChecksInitial);
  const [buriedQuestions, setBuriedQuestions] = useState<BuriedChecksPolar>(buriedInitial);
  const [axialQuestions, setAxialQuestions] = useState<AxialLoadsPolar>(axialInitial);
  const [vacuumQuestions, setVacuumQuestions] = useState<VacuumPolar>(vacuumInitial);
  const [pressureCyclesQuestion, setPressureCyclesQuestion] = useState<PressureCyclesPolar>(pressureCyclesInitial);
  const [strainsChecksQuestions, setStrainsChecksQuestions] = useState<StrainsChecksPolar>(strainsInitial);
  const [excludeComponentStrengthQuestions, setExcludeComponentStrengthQuestions] =
    useState<ExcludeComponentStrengthPolar>(excludeComponentStrengthInitial);

  const { setChecks, sections } = useCUCompositeJobStore();
  const savedChecksSection = sections.checksSection;
  const savedData = savedChecksSection.data as ChecksLongData;

  // derived state check if values for sub check polar questions is filled
  const utReadingFilled =
    !!utQuestions.calculate_at_worst_case ||
    utQuestions.is_ut_reading_less_than_04 === "no" ||
    !!utQuestions.per_code_line_leaking_do_you_know_diameter;
  const leakingFilled = leakingQuestions.component_patched_or_repaired === "no" || !!leakingQuestions.is_pinhole_leak;
  const shutDownFilled = !!shutDownQuestions.is_component_shutdown_during_repair;
  const extraLayersFilled = !!extraLayesQuestions.add_extraLayers;
  const buriedFilled = !!buriedQuestions.is_component_buried;
  const axialFilled =
    axialQuestions.design_axial_loads === "no" ||
    axialQuestions.is_component_straight_line === "no" ||
    axialQuestions.do_you_want_to_take_into_account_any_additional_forces_and_moments;
  const vacuumFilled = !!vacuumQuestions.do_you_want_to_design_for_external_loads_or_vacuum;
  const pressureFilled =
    pressureCyclesQuestion.do_you_want_to_design_for_constant_pressure_cycles === "no" ||
    !!pressureCyclesQuestion.are_the_expected_cycles_more_than_7000_over_design_life;
  const strainsFilled = !!strainsChecksQuestions.do_you_want_to_design_for_continous_long_term_strains;
  const noLastQuestionInAxial = axialQuestions.is_component_straight_line !== "yes";
  const excludeComponentsFilled =
    excludeComponentStrengthQuestions.do_you_want_to_take_into_account_any_additional_forces_and_moments ||
    excludeComponentStrengthQuestions.do_you_want_to_completely_exclude_the_components_strength_for_design === "no" ||
    (excludeComponentStrengthQuestions.do_you_want_to_completely_exclude_the_components_strength_for_design === "yes" &&
      axialQuestions.do_you_want_to_take_into_account_any_additional_forces_and_moments);
  const areAllQuestionsAnswered =
    proceedWithOtherChecksQuestions.do_you_wish_to_answer_more_questions === "no" ||
    (proceedWithOtherChecksQuestions.do_you_wish_to_answer_more_questions === "yes" && excludeComponentsFilled);

  // console.log(
  //   {
  //     utReadingFilled,
  //     leakingFilled,
  //     shutDownFilled,
  //     extraLayersFilled,
  //     buriedFilled,
  //     axialFilled,
  //     vacuumFilled,
  //     pressureFilled,
  //     strainsFilled,
  //     excludeComponentsFilled,
  //     excludeComponentStrengthQuestions,
  //     noLastQuestionInAxial,
  //     axialQuestions,
  //     areAllQuestionsAnswered,
  //   },
  //   "here lets check",
  // );

  useEffect(() => {
    if (savedChecksSection.isDataFilled && !utReadingFilled) {
      setUtQuestions({
        do_you_have_ut_reading_info: savedData.do_you_have_ut_reading_info,
        calculate_at_worst_case: savedData.calculate_at_worst_case,
        is_ut_reading_less_than_04: savedData.is_ut_reading_less_than_04,
        per_code_line_leaking_do_you_know_diameter: savedData.per_code_line_leaking_do_you_know_diameter,
      });
      setLeakingQuestions({
        component_leaking: savedData.component_leaking,
        is_pinhole_leak: savedData.is_pinhole_leak,
        component_patched_or_repaired: savedData.component_patched_or_repaired,
      });
      setShutDownQuestions({
        is_component_shutdown_during_repair: savedData.is_component_shutdown_during_repair,
      });
      setExtraLayesQuestions({
        add_extraLayers: savedData.add_extraLayers,
      });
      setProceedWithOtherChecks({
        do_you_wish_to_answer_more_questions: savedData.do_you_wish_to_answer_more_questions,
      });
      setBuriedQuestions({
        is_component_buried: savedData.is_component_buried,
      });
      setAxialQuestions({
        design_axial_loads: savedData.design_axial_loads,
        is_component_straight_line: savedData.is_component_straight_line,
        do_you_want_to_take_into_account_any_additional_forces_and_moments:
          savedData.do_you_want_to_take_into_account_any_additional_forces_and_moments,
      });
      setVacuumQuestions({
        do_you_want_to_design_for_external_loads_or_vacuum:
          savedData.do_you_want_to_design_for_external_loads_or_vacuum,
      });
      setPressureCyclesQuestion({
        do_you_want_to_design_for_constant_pressure_cycles:
          savedData.do_you_want_to_design_for_constant_pressure_cycles,
        are_the_expected_cycles_more_than_7000_over_design_life:
          savedData.are_the_expected_cycles_more_than_7000_over_design_life,
      });
      setStrainsChecksQuestions({
        do_you_want_to_design_for_continous_long_term_strains:
          savedData.do_you_want_to_design_for_continous_long_term_strains,
      });
      setExcludeComponentStrengthQuestions({
        do_you_want_to_completely_exclude_the_components_strength_for_design:
          savedData.do_you_want_to_completely_exclude_the_components_strength_for_design,
        do_you_want_to_take_into_account_any_additional_forces_and_moments:
          savedData.do_you_want_to_take_into_account_any_additional_forces_and_moments,
      });
    }
  }, [savedChecksSection]);

  function removeEmpty(obj: object) {
    return Object.entries(obj)
      .filter(([_, v]) => v != null)
      .reduce((acc, [k, v]) => ({ ...acc, [k]: v }), {});
  }

  const handleSubmitSection = () => {
    const data = [
      utQuestions,
      leakingQuestions,
      shutDownQuestions,
      extraLayesQuestions,
      buriedQuestions,
      axialQuestions,
      proceedWithOtherChecksQuestions,
      vacuumQuestions,
      pressureCyclesQuestion,
      strainsChecksQuestions,
      excludeComponentStrengthQuestions,
    ].map(removeEmpty);
    setChecks({
      status: "done",
      data: mergeAll(data) as ChecksLongData,
    });
  };

  const handleUt = ({ key, value }: { value: PolarYesNoUndefined; key: keyof UtPolar }) => {
    setUtQuestions((prevState) => {
      if (key === "do_you_have_ut_reading_info") {
        return {
          ...utInitial,
          do_you_have_ut_reading_info: value,
        };
      } else if (key === "calculate_at_worst_case") {
        return {
          do_you_have_ut_reading_info: prevState.do_you_have_ut_reading_info,
          calculate_at_worst_case: value,
          is_ut_reading_less_than_04: undefined,
          per_code_line_leaking_do_you_know_diameter: undefined,
        };
      } else if (key === "is_ut_reading_less_than_04") {
        return {
          do_you_have_ut_reading_info: prevState.do_you_have_ut_reading_info,
          calculate_at_worst_case: undefined,
          is_ut_reading_less_than_04: value,
          per_code_line_leaking_do_you_know_diameter: undefined,
        };
      }
      return {
        do_you_have_ut_reading_info: prevState.do_you_have_ut_reading_info,
        calculate_at_worst_case: undefined,
        is_ut_reading_less_than_04: prevState.is_ut_reading_less_than_04,
        per_code_line_leaking_do_you_know_diameter: value,
      };
    });

    //reset next sub sections
    setLeakingQuestions(leakingInitial);
    setShutDownQuestions(shutDownInitial);
    setExtraLayesQuestions(extraLayesInitial);
    setProceedWithOtherChecks(proceedWithOtherChecksInitial);
    setBuriedQuestions(buriedInitial);
    setAxialQuestions(axialInitial);
    setVacuumQuestions(vacuumInitial);
    setPressureCyclesQuestion(pressureCyclesInitial);
    setStrainsChecksQuestions(strainsInitial);
    setExcludeComponentStrengthQuestions(excludeComponentStrengthInitial);
  };
  const handleLeaking = ({ value, key }: { value: PolarYesNoUndefined; key: keyof LeakingPolar }) => {
    setLeakingQuestions((prevState) => {
      if (key === "component_leaking") {
        return {
          ...leakingInitial,
          component_leaking: value,
        };
      } else if (key === "component_patched_or_repaired") {
        return {
          component_leaking: prevState.component_leaking,
          is_pinhole_leak: undefined,
          component_patched_or_repaired: value,
        };
      }
      return {
        ...prevState,
        is_pinhole_leak: value,
      };
    });
    //reset next sub sections
    setShutDownQuestions(shutDownInitial);
    setExtraLayesQuestions(extraLayesInitial);
    setProceedWithOtherChecks(proceedWithOtherChecksInitial);
    setBuriedQuestions(buriedInitial);
    setAxialQuestions(axialInitial);
    setVacuumQuestions(vacuumInitial);
    setPressureCyclesQuestion(pressureCyclesInitial);
    setStrainsChecksQuestions(strainsInitial);
    setExcludeComponentStrengthQuestions(excludeComponentStrengthInitial);
  };
  const handleShutDowns = ({ value }: { value: PolarYesNoUndefined; key: keyof ShutDownPolar }) => {
    setShutDownQuestions({ is_component_shutdown_during_repair: value });
    //reset next sub sections
    setExtraLayesQuestions(extraLayesInitial);
    setProceedWithOtherChecks(proceedWithOtherChecksInitial);
    setBuriedQuestions(buriedInitial);
    setAxialQuestions(axialInitial);
    setVacuumQuestions(vacuumInitial);
    setPressureCyclesQuestion(pressureCyclesInitial);
    setStrainsChecksQuestions(strainsInitial);
    setExcludeComponentStrengthQuestions(excludeComponentStrengthInitial);
  };
  const handleExtraLayes = ({ value }: { value: PolarYesNoUndefined; key: keyof ExtraLayesPolar }) => {
    setExtraLayesQuestions({ add_extraLayers: value });
    //reset next sub sections
    setProceedWithOtherChecks(proceedWithOtherChecksInitial);
    setBuriedQuestions(buriedInitial);
    setAxialQuestions(axialInitial);
    setVacuumQuestions(vacuumInitial);
    setPressureCyclesQuestion(pressureCyclesInitial);
    setStrainsChecksQuestions(strainsInitial);
    setExcludeComponentStrengthQuestions(excludeComponentStrengthInitial);
  };

  const handleProceed = ({ value }: { value: PolarYesNoUndefined; key: keyof ProceedWithNextPolar }) => {
    setProceedWithOtherChecks({ do_you_wish_to_answer_more_questions: value });
    //reset next sub sections
    setBuriedQuestions(buriedInitial);
    setAxialQuestions(axialInitial);
    setVacuumQuestions(vacuumInitial);
    setPressureCyclesQuestion(pressureCyclesInitial);
    setStrainsChecksQuestions(strainsInitial);
    setExcludeComponentStrengthQuestions(excludeComponentStrengthInitial);
  };
  const handleBuried = ({ value }: { value: PolarYesNoUndefined; key: keyof BuriedChecksPolar }) => {
    setBuriedQuestions({ is_component_buried: value });
    //reset next sub sections
    setAxialQuestions(axialInitial);
    setVacuumQuestions(vacuumInitial);
    setPressureCyclesQuestion(pressureCyclesInitial);
    setStrainsChecksQuestions(strainsInitial);
    setExcludeComponentStrengthQuestions(excludeComponentStrengthInitial);
  };

  const handleAxial = ({ key, value }: { value: PolarYesNoUndefined; key: keyof AxialLoadsPolar }) => {
    setAxialQuestions((prevState) => {
      if (key === "design_axial_loads") {
        return {
          ...axialInitial,
          [key]: value,
        };
      }
      if (key === "is_component_straight_line") {
        return {
          design_axial_loads: prevState.design_axial_loads,
          is_component_straight_line: value,
          do_you_want_to_take_into_account_any_additional_forces_and_moments: undefined,
        };
      }
      return {
        ...prevState,
        [key]: value,
      };
    });
    //reset next sub sections
    setVacuumQuestions(vacuumInitial);
    setPressureCyclesQuestion(pressureCyclesInitial);
    setStrainsChecksQuestions(strainsInitial);
    setExcludeComponentStrengthQuestions(excludeComponentStrengthInitial);
  };

  const handleVacuum = ({ key, value }: { value: PolarYesNoUndefined; key: keyof VacuumPolar }) => {
    setVacuumQuestions({ [key]: value });
    //reset next sub sections
    setPressureCyclesQuestion(pressureCyclesInitial);
    setStrainsChecksQuestions(strainsInitial);
    setExcludeComponentStrengthQuestions(excludeComponentStrengthInitial);
  };

  const handlePressureCycles = ({ key, value }: { value: PolarYesNoUndefined; key: keyof PressureCyclesPolar }) => {
    setPressureCyclesQuestion((prevState) => {
      if (key === "do_you_want_to_design_for_constant_pressure_cycles") {
        return {
          ...pressureCyclesInitial,
          [key]: value,
        };
      } else {
        return {
          ...prevState,
          [key]: value,
        };
      }
    });
    //reset next sub sections
    setStrainsChecksQuestions(strainsInitial);
    setExcludeComponentStrengthQuestions(excludeComponentStrengthInitial);
  };

  const handleStrains = ({ key, value }: { value: PolarYesNoUndefined; key: keyof StrainsChecksPolar }) => {
    setStrainsChecksQuestions({ [key]: value });
    //reset next sub sections
    setExcludeComponentStrengthQuestions(excludeComponentStrengthInitial);
  };

  const handleExcludeComponentStrength = ({
    key,
    value,
  }: {
    value: PolarYesNoUndefined;
    key: keyof ExcludeComponentStrengthPolar;
  }) => {
    setExcludeComponentStrengthQuestions((prevState) => {
      if (key === "do_you_want_to_completely_exclude_the_components_strength_for_design") {
        return {
          ...excludeComponentStrengthInitial,
          [key]: value,
        };
      }
      return {
        ...prevState,
        [key]: value,
      };
    });
  };

  return (
    <div className="flex flex-col">
      <UtChecks onHandleYesNo={handleUt} polarQuestions={utQuestions} />
      {utReadingFilled && <LeakingChecks onHandleYesNo={handleLeaking} polarQuestions={leakingQuestions} />}
      {leakingFilled && <ShutDownChecks onHandleYesNo={handleShutDowns} polarQuestions={shutDownQuestions} />}
      {shutDownFilled && <ExtraLayersChecks onHandleYesNo={handleExtraLayes} polarQuestions={extraLayesQuestions} />}
      {extraLayersFilled && (
        <ProceedWithOtherChecks onHandleYesNo={handleProceed} polarQuestions={proceedWithOtherChecksQuestions} />
      )}
      {proceedWithOtherChecksQuestions.do_you_wish_to_answer_more_questions === "yes" && (
        <>
          <BuriedChecks onHandleYesNo={handleBuried} polarQuestions={buriedQuestions} />
          {buriedFilled && <AxialLoadsChecks onHandleYesNo={handleAxial} polarQuestions={axialQuestions} />}
          {axialFilled && <VacuumChecks onHandleYesNo={handleVacuum} polarQuestions={vacuumQuestions} />}
          {vacuumFilled && (
            <PressureCyclesChecks onHandleYesNo={handlePressureCycles} polarQuestions={pressureCyclesQuestion} />
          )}
          {pressureFilled && <StrainsChecks onHandleYesNo={handleStrains} polarQuestions={strainsChecksQuestions} />}
          {strainsFilled && (
            <ExcludeComponentStrengthChecks
              axialLoadsQuestions={axialQuestions}
              onHandleYesNo={handleExcludeComponentStrength}
              polarQuestions={excludeComponentStrengthQuestions}
            />
          )}
        </>
      )}
      <Button className="ml-auto mt-4" disabled={!areAllQuestionsAnswered} onClick={handleSubmitSection}>
        Save
      </Button>
    </div>
  );
}
